import firebase from "firebase";
import ChatModel from "@/models/Chat";
import _debug from "debug";

const debug = _debug("Mediacao:components:chat:SingleChatController.ts");

function sameId(a: any, b: any): boolean {
    return a.id === b.id;
}

function hasElement(array: any[], element: any, comparator: (a: any, b: any) => boolean): boolean {
    return array.some(item => {
        return comparator(element, item);
    });
}

function getElement(array: any[], elmentRecognizer: (testedElement: any) => boolean): any {
    let element: any = null;
    array.some(item => {
        if (elmentRecognizer(item)) {
            element = item;
            return true;
        }
        return false;
    });
    return element;
}

function getElementIndex(array: any[], elmentRecognizer: (testedElement: any) => boolean): number {
    for (let i = 0; i < array.length; i++) {
        if (elmentRecognizer(array[i])) {
            return i;
        }
    }
    return -1;
}

function remove(array: any[], elmentRecognizer: (testedElement: any) => boolean): any {
    for (let i = 0; i < array.length; i++) {
        if (elmentRecognizer(array[i])) {
            return array.splice(i, 1);
        }
    }
    return null;
}

export interface MensagemChatDb {
    id?: string;
    dataHora: Date;
    mensagem: string;
    usuario?: string;
    tipoChat: string;
    url?: string;
    tipoArquivo?: string;
    storageUri?: string;
}
export interface MensagemChatDados extends MensagemChatDb {
    sortId?: number;
    nomeUsuario?: string;
    tipoUsuario?: string;
}

export interface MensagemListener {
    id: number;
    novaMensagem(mensagem: MensagemChatDados): void;
    mensagemAlterada(mensagem: MensagemChatDados): void;
    mensagemRemovida(id: string): void;
}

export default class SingleChatController {
    public get chat() {
        return this.myChat;
    }
    public mensagensPreparadas: MensagemChatDados[] = [];
    private myChat: ChatModel;
    private messageListenerUnsubscribe?: () => void;
    private mensagemListeners: MensagemListener[] = [];

    constructor(chat: ChatModel) {
        this.myChat = chat;
        console.log("SingleChatController constructor");
    }
    public async loadMessagesFromDatabase() {
        debug("loadMessagesFromDatabase");
        if (this.myChat && this.myChat.processo) {
            const querySnapShot = await firebase
                .firestore()
                .collection(`processos/${this.myChat.processo.id}/chatsProcesso/${this.myChat.id}/mensagensChatProcesso`)
                .get();
            querySnapShot.forEach(doc => {
                this.novaMensagem(this.myChat, doc);
            });
        }
    }

    public startListeningToDatabase() {
        if (this.myChat && this.myChat.processo) {
            this.messageListenerUnsubscribe = firebase
                .firestore()
                .collection(`processos/${this.myChat.processo.id}/chatsProcesso/${this.myChat.id}/mensagensChatProcesso`)
                .onSnapshot(mensagemSnapshot => {
                    mensagemSnapshot.docChanges().forEach(mensagemDocChange => {
                        debug("mensagemDocChange", mensagemDocChange);
                        if (mensagemDocChange.type === "added") {
                            if (!mensagemDocChange.doc.metadata.hasPendingWrites) {
                                this.novaMensagem(this.myChat, mensagemDocChange.doc);
                            } else {
                                console.log("Nova mensagem minha, vou esperar voltar do servidor.");
                            }
                        } else if (mensagemDocChange.type === "modified") {
                            this.updateMensagem(this.myChat, mensagemDocChange.doc);
                        } else if (mensagemDocChange.type === "removed") {
                            this.removeMensagem(this.myChat, mensagemDocChange.doc);
                        }
                    });
                });
            debug("SingleChatController.startListeningToDatabase", this.messageListenerUnsubscribe);
        }
    }

    public addNovaMensagemListener(listener: MensagemListener) {
        if (!hasElement(this.mensagemListeners, listener, sameId)) {
            this.mensagemListeners.push(listener);
        }
        console.log("SingleChatController addNovaMensagemListener");
    }
    public removeNovaMensagemListener(listener: MensagemListener) {
        remove(this.mensagemListeners, lt => lt.id === listener.id);
    }

    public close() {
        if (this.messageListenerUnsubscribe) {
            this.messageListenerUnsubscribe();
        }
    }

    public async encerrarChat() {
        if (this.myChat && this.myChat.processo) {
            const ref = firebase.firestore().doc(`processos/${this.myChat.processo.id}/chatsProcesso/${this.myChat.id}`);
            await ref.update({ status: "encerrado" });
        }
    }

    public enviarMensagem(mensagem: MensagemChatDb): Promise<firebase.firestore.DocumentReference<firebase.firestore.DocumentData> | null> {
        if (this.myChat && this.myChat.processo) {
            const ref = firebase.firestore().collection(`processos/${this.myChat.processo.id}/chatsProcesso/${this.myChat.id}/mensagensChatProcesso`);
            const mensagemHorarioServidor: any = Object.assign({}, mensagem);
            mensagemHorarioServidor.dataHora = firebase.firestore.FieldValue.serverTimestamp();
            return ref.add(mensagemHorarioServidor);
        }
        return Promise.resolve(null);
    }

    public enviarMensagemArquivo(mensagem: MensagemChatDb, file: File) {
        return this.enviarMensagem(mensagem).then((messageRef: firebase.firestore.DocumentReference<firebase.firestore.DocumentData> | null) => {
            if (messageRef) {
                const filePath = messageRef.path + "/" + file.name;

                return firebase
                    .storage()
                    .ref(filePath)
                    .put(file)
                    .then(function(fileSnapshot) {
                        // 3 - Generate a public URL for the file.
                        return fileSnapshot.ref.getDownloadURL().then(url => {
                            // 4 - Update the chat message placeholder with the image’s URL.
                            return messageRef.update({
                                url: url,
                                storageUri: fileSnapshot.metadata.fullPath,
                            });
                        });
                    });
            }
        });
    }

    private novaMensagem(chat: ChatModel, mensagemDoc: firebase.firestore.QueryDocumentSnapshot) {
        const mensagemDb: unknown = Object.assign({ id: mensagemDoc.id }, mensagemDoc.data());
        debug("Nova mensagen, mensagem, chat", mensagemDoc.data(), chat);
        this.preparaMensagem(mensagemDb as MensagemChatDados);
    }
    private updateMensagem(chat: ChatModel, mensagemDoc: firebase.firestore.QueryDocumentSnapshot) {
        const mensagemDb: unknown = Object.assign({ id: mensagemDoc.id }, mensagemDoc.data());
        debug("Mensagem alterada, mensagem, chat", mensagemDoc.data(), chat);
        this.preparaMensagem(mensagemDb as MensagemChatDados);
    }

    private removeMensagem(chat: ChatModel, mensagemDoc: firebase.firestore.QueryDocumentSnapshot) {
        const mensagemRemovida: string = mensagemDoc.id;
        debug("Mensagem removida, mensagem, chat", mensagemDoc, chat);
        this.mensagemListeners.forEach(listener => {
            listener.mensagemRemovida(mensagemRemovida);
        });
    }
    private async preparaMensagem(mensagemDb: MensagemChatDados) {
        if (mensagemDb.dataHora) {
            const dataHora: firebase.firestore.Timestamp = new firebase.firestore.Timestamp((mensagemDb.dataHora as any).seconds, (mensagemDb.dataHora as any).nanoseconds);
            mensagemDb.dataHora = dataHora.toDate();
            mensagemDb.sortId = mensagemDb.dataHora.getTime();
        } else {
            mensagemDb.dataHora = new Date();
            mensagemDb.sortId = mensagemDb.dataHora.getTime();
        }
        console.log("preparaMensagem");

        const user = await this.getUser(mensagemDb.usuario);
        if (user) {
            mensagemDb.tipoUsuario = user.tipo;
            mensagemDb.nomeUsuario = user.user.name;
            if (hasElement(this.mensagensPreparadas, mensagemDb, (a: any, b: any) => a.id === b.id)) {
                const msg = getElement(this.mensagensPreparadas, mensagem => {
                    return mensagem.id === mensagemDb.id;
                });
                console.log("mensagem alterada, old, new", msg, mensagemDb);
                if (msg) {
                    const keys = Object.keys(mensagemDb);
                    for (const key of keys) {
                        msg[key] = (mensagemDb as any)[key];
                    }
                    this.mensagemListeners.forEach(listener => {
                        listener.mensagemAlterada(mensagemDb);
                    });
                }
            } else {
                this.mensagensPreparadas.push(mensagemDb);
                debug("Adicionando mensagem!", mensagemDb);
                this.mensagemListeners.forEach(listener => {
                    listener.novaMensagem(mensagemDb);
                });
            }
        }
    }

    private async getUser(userRef?: string) {
        if (this.myChat && this.myChat.processo && userRef) {
            const partes: any = await this.myChat.processo.partesPromise;
            if (partes) {
                const tipos = Object.keys(partes);
                for (const tipo of tipos) {
                    const user = partes[tipo];
                    if (user && userRef === user.email) {
                        return { tipo, user };
                    }
                }
            }
        }
        return null;
    }
}
